import React, { useEffect, useMemo } from "react";

import { IVenue } from "Venues/types";

import { copy } from "shared/assets";
import { Card, LinkButton } from "shared/components";
import { Spacer } from "shared/layout";
import { ICardTheme, ITheme } from "shared/types";
import {
  AddressInfo,
  ContactInfo,
  LastUpdatedInfo,
  Logo,
  StageInfo,
  VenueTitle,
} from "Venues/components";
import { getVenueUrlWithEvent, useScrollIntoView, useVenueMap } from "Venues/utils";
import { StyledVenueSpacer, StyledVenueTeaser } from "./style";

interface VenueTeaserProps extends IVenue {
  isSmallView?: boolean;
  isActive?: boolean;
  onClick?: () => void;
  theme?: ICardTheme;
  showMoreInfo?: boolean;
  showMap?: boolean;
  hideContainer?: boolean;
  openInNewTab?: boolean;
  eventId?: string;
}

export const VenueTeaser: React.FC<VenueTeaserProps> = ({
  id,
  title,
  alternativeName,
  address,
  meta,
  updatedAt,
  logo,
  stages,
  isActive,
  isSmallView,
  onClick,
  theme,
  showMoreInfo = true,
  showMap,
  hideContainer,
  openInNewTab,
  eventId,
  children,
  contact,
}) => {
  const { ref, scrollIntoView } = useScrollIntoView();

  const cardTheme: ICardTheme = useMemo(() => {
    if (isActive) {
      return ICardTheme.secondary;
    }

    return theme || ICardTheme.primary;
  }, [theme, isActive]);

  const venueUrl = useMemo(() => getVenueUrlWithEvent(id, eventId), [eventId, id]);

  useEffect(() => {
    if (isActive) {
      scrollIntoView();
    }
  }, [isActive, scrollIntoView]);

  const { renderMapModal, renderMapPreview } = useVenueMap({ title, address });

  return (
    <StyledVenueTeaser>
      <Card theme={cardTheme} hideContainer={hideContainer} data-testid="venue_teaser">
        <div onClick={onClick} ref={ref}>
          <Spacer size="m">
            <Spacer
              size="s"
              spaceDirection="horizontal"
              justifyContent="space-between"
              flexDirection={isSmallView ? "column-reverse" : undefined}
              align={isSmallView ? "flex-start" : undefined}
              safePrint={true}
            >
              <StyledVenueSpacer size="m">
                <VenueTitle
                  title={title}
                  alternativeName={alternativeName}
                  meta={meta}
                  venueUrl={venueUrl}
                  openInNewTab={openInNewTab}
                />
                <div>
                  <AddressInfo address={address} />
                </div>
                <div>
                  <ContactInfo {...contact}></ContactInfo>
                </div>
                <StageInfo stages={stages} venueId={id} eventId={eventId} />
              </StyledVenueSpacer>
              <Spacer
                size="s"
                spaceDirection={isSmallView ? "vertical" : "horizontal"}
                align={isSmallView ? "center" : "flex-start"}
              >
                {logo && <Logo logo={logo} alt="" />}
                {showMap && (
                  <>
                    {renderMapPreview()}
                    {renderMapModal()}
                  </>
                )}
              </Spacer>
            </Spacer>
            <Spacer
              size="s"
              spaceDirection={isSmallView ? "vertical" : "horizontal"}
              align={isSmallView ? "flex-start" : "center"}
            >
              {showMoreInfo && (
                <span onClick={e => e.stopPropagation()}>
                  <LinkButton to={venueUrl} size="m" theme={ITheme.secondary}>
                    {copy.searchPage.venue.moreInfo}
                  </LinkButton>
                </span>
              )}
              {updatedAt && <LastUpdatedInfo time={updatedAt} />}
            </Spacer>
            {children}
          </Spacer>
        </div>
      </Card>
    </StyledVenueTeaser>
  );
};
